import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Trash2, Loader2, X, Wallet } from 'lucide-react';
import { useT } from '../../utils/translate';
import { fmtNumber } from '../../utils/format';
import { formatDate } from '../../utils/date';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const METHODS = ['cash', 'transfer', 'card'];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function Dialog({ title, children, onClose, onSubmit, busy }) {
  const t = useT();
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={e => { e.preventDefault(); onSubmit(); }}
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-modal w-full sm:max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-100">
          <h2 className="font-semibold text-ink">{title}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-tertiary"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-surface-100">
          <button type="button" onClick={onClose} className="btn-secondary btn-md">{t('dealFinance.cancel')}</button>
          <button type="submit" disabled={busy} className="btn-primary btn-md flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t('dealFinance.save')}
          </button>
        </div>
      </form>
    </div>
  );
}

// Bitimdagi to'lovlar: kelishilgan summa, qo'shimcha sotuvlar, to'langan, qarz
export default function DealPayments({ deal, funnelId, currency, onChange }) {
  const t = useT();
  const user = useSelector(s => s.auth.user);
  const isManager = ['owner', 'admin'].includes(user?.role);
  const [dialog, setDialog] = useState(null);   // 'payment' | 'charge'
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const base = `${API}/funnels/${funnelId}/deals/${deal._id}`;
  const open = (kind) => {
    setForm(kind === 'payment' ? { amount: '', date: today(), method: 'cash', note: '' } : { title: '', amount: '' });
    setDialog(kind);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${base}/${dialog === 'payment' ? 'payments' : 'charges'}`, { ...form, amount: Number(form.amount) });
      onChange(res.data.deal);
      toast.success(t(dialog === 'payment' ? 'dealFinance.paymentAdded' : 'dealFinance.chargeAdded'));
      setDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.message || t('dealFinance.error'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (kind, id) => {
    if (!window.confirm(t('dealFinance.deleteConfirm'))) return;
    try {
      const res = await axios.delete(`${base}/${kind}/${id}`);
      onChange(res.data.deal);
    } catch (e) {
      toast.error(e.response?.data?.message || t('dealFinance.error'));
    }
  };
  const money = (n) => `${fmtNumber(Math.round((n || 0) * 100) / 100)} ${currency}`;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const payments = [...(deal.payments || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div>
      <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
        <Wallet className="w-3.5 h-3.5" />{t('dealFinance.title')}
      </p>
      <div className="border border-surface-100 rounded-xl overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-surface-50">
          <div><div className="text-xs text-ink-tertiary">{t('dealFinance.agreed')}</div><div className="text-sm font-semibold tabular-nums">{money(deal.value)}</div></div>
          <div><div className="text-xs text-ink-tertiary">{t('dealFinance.extra')}</div><div className="text-sm font-semibold tabular-nums">{money(deal.chargesTotal)}</div></div>
          <div><div className="text-xs text-ink-tertiary">{t('dealFinance.paid')}</div><div className="text-sm font-semibold tabular-nums text-emerald-700">{money(deal.paidTotal)}</div></div>
          <div><div className="text-xs text-ink-tertiary">{t('dealFinance.debt')}</div>
            <div className={`text-sm font-bold tabular-nums ${deal.debt > 0 ? 'text-red-600' : 'text-ink'}`}>{money(Math.max(0, deal.debt || 0))}</div></div>
        </div>
        <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-surface-100">
          <button type="button" onClick={() => open('payment')} className="btn-primary btn-sm flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />{t('dealFinance.addPayment')}</button>
          <button type="button" onClick={() => open('charge')} className="btn-secondary btn-sm flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />{t('dealFinance.addCharge')}</button>
        </div>
        {(payments.length > 0 || (deal.extraCharges || []).length > 0) && (
          <ul className="divide-y divide-surface-100 border-t border-surface-100 text-sm">
            {payments.map(p => (
              <li key={p._id} className="flex items-center justify-between gap-2 px-4 py-2">
                <span className="min-w-0">
                  <span className="font-medium text-emerald-700 tabular-nums">+{money(p.amount)}</span>
                  <span className="ml-2 text-xs text-ink-tertiary">{formatDate(p.date)} · {t(`dealFinance.method.${p.method}`)}{p.note ? ` · ${p.note}` : ''}</span>
                </span>
                {isManager && (
                  <button type="button" onClick={() => remove('payments', p._id)} className="p-1 rounded text-ink-tertiary hover:text-red-600 hover:bg-red-50" aria-label={t('dealFinance.delete')}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
            {(deal.extraCharges || []).map(c => (
              <li key={c._id} className="flex items-center justify-between gap-2 px-4 py-2">
                <span className="min-w-0">
                  <span className="font-medium tabular-nums">{c.title}: {money(c.amount)}</span>
                  <span className="ml-2 text-xs text-ink-tertiary">{t('dealFinance.extraSale')} · {formatDate(c.createdAt)}</span>
                </span>
                {(isManager || String(c.createdBy) === String(user?.id || user?._id)) && (
                  <button type="button" onClick={() => remove('charges', c._id)} className="p-1 rounded text-ink-tertiary hover:text-red-600 hover:bg-red-50" aria-label={t('dealFinance.delete')}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialog === 'payment' && (
        <Dialog title={t('dealFinance.addPayment')} onClose={() => setDialog(null)} onSubmit={submit} busy={busy}>
          <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.amount')}, {currency}</span>
            <input className="input" type="number" min="0" step="0.01" autoFocus required value={form.amount} onChange={e => set('amount', e.target.value)} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.date')}</span>
              <input className="input" type="date" value={form.date} onChange={e => set('date', e.target.value)} /></label>
            <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.methodLabel')}</span>
              <select className="input" value={form.method} onChange={e => set('method', e.target.value)}>
                {METHODS.map(m => <option key={m} value={m}>{t(`dealFinance.method.${m}`)}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.note')}</span>
            <input className="input" value={form.note} onChange={e => set('note', e.target.value)} /></label>
        </Dialog>
      )}
      {dialog === 'charge' && (
        <Dialog title={t('dealFinance.addCharge')} onClose={() => setDialog(null)} onSubmit={submit} busy={busy}>
          <p className="text-xs text-ink-tertiary">{t('dealFinance.chargeHint')}</p>
          <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.chargeTitle')}</span>
            <input className="input" autoFocus required value={form.title} onChange={e => set('title', e.target.value)} /></label>
          <label className="block"><span className="block text-sm font-medium text-ink mb-1">{t('dealFinance.amount')}, {currency}</span>
            <input className="input" type="number" min="0" step="0.01" required value={form.amount} onChange={e => set('amount', e.target.value)} /></label>
        </Dialog>
      )}
    </div>
  );
}
